'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

export default function MBWPage() {
  const [isDark, setIsDark] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profit, setProfit] = useState(250000);

  // Tax calculations
  const gerTax = profit * 0.42;
  const bulgTax = profit * 0.10;
  const totalSavings = gerTax - bulgTax;

  function formatCurrency(val: number) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(val);
  }

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('mbw-theme') : null;
    if (stored === 'light') setIsDark(false);
    else setIsDark(true);
  }, []);

  function toggleTheme() {
    const next = !isDark;
    setIsDark(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('mbw-theme', next ? 'dark' : 'light');
    }
  }

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const theme = {
    bg: isDark ? 'bg-[#070A10]' : 'bg-slate-50',
    text: isDark ? 'text-slate-100' : 'text-slate-900',
    textMuted: isDark ? 'text-slate-400' : 'text-slate-600',
    textSubtle: isDark ? 'text-slate-500' : 'text-slate-500',
    border: isDark ? 'border-white/5' : 'border-slate-200',
    cardBg: isDark ? 'bg-[#0B0F19]/60' : 'bg-white',
    sectionBg: isDark ? 'bg-[#0B0F19]/30' : 'bg-slate-100',
    inputBg: isDark ? 'bg-[#070A10]' : 'bg-slate-50',
    headerBg: isDark ? 'bg-[#070A10]/80' : 'bg-white/80',
    mobileMenuBg: isDark ? 'bg-[#0B0F19]/95' : 'bg-white/98',
    navText: isDark ? 'text-slate-400' : 'text-slate-600',
    mobileNavText: isDark ? 'text-slate-300' : 'text-slate-800',
  };

  return (
    <div
      className={`${theme.bg} ${theme.text} antialiased font-jakarta min-h-screen`}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {/* Top Glow */}
      {isDark && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[500px] bg-gradient-to-b from-[#C5A880]/10 via-transparent to-transparent blur-3xl pointer-events-none" />
      )}

      {/* HEADER */}
      <header
        className={`fixed top-0 w-full z-50 ${theme.headerBg} backdrop-blur-md border-b ${theme.border} transition-all`}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 group">
            <svg
              className="w-10 h-10 text-[#C5A880] transition-transform duration-300 group-hover:scale-105"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M20 75V25L40 50L60 25V75" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M40 75V50L60 75" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M15 85H85" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M15 15H85" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <div className="flex flex-col">
              <span className={`text-lg font-bold tracking-widest ${theme.text}`}>MBW</span>
              <span className="text-[9px] uppercase tracking-[0.3em] text-[#C5A880]">International</span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className={`hidden md:flex items-center gap-8 text-sm font-medium ${theme.navText}`}>
            <a href="#vorgaben" className="hover:text-[#C5A880] transition-colors">Compliance</a>
            <a href="#mythen" className="hover:text-[#C5A880] transition-colors">Mythen-Check</a>
            <a href="#rechner" className="hover:text-[#C5A880] transition-colors">Vorteils-Rechner</a>
            <a href="#prozess" className="hover:text-[#C5A880] transition-colors">Ablauf</a>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className={`p-2.5 rounded-full border ${theme.border} hover:bg-white/5 transition-all ${theme.navText}`}
              aria-label="Farbschema wechseln"
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            <a
              href="#kontakt"
              className="hidden sm:inline-flex bg-gradient-to-r from-[#C5A880] to-[#A0825B] text-[#070A10] px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Erstgespräch buchen
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 ${theme.navText}`}
              aria-label="Menü öffnen"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      <div
        className={`fixed inset-0 z-40 ${theme.mobileMenuBg} backdrop-blur-lg transition-transform duration-300 md:hidden flex flex-col justify-between p-8 pt-28 ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <nav className="flex flex-col gap-6 text-xl font-semibold">
          <a href="#vorgaben" onClick={closeMobileMenu} className={`${theme.mobileNavText} hover:text-[#C5A880]`}>Compliance</a>
          <a href="#mythen" onClick={closeMobileMenu} className={`${theme.mobileNavText} hover:text-[#C5A880]`}>Mythen-Check</a>
          <a href="#rechner" onClick={closeMobileMenu} className={`${theme.mobileNavText} hover:text-[#C5A880]`}>Vorteils-Rechner</a>
          <a href="#prozess" onClick={closeMobileMenu} className={`${theme.mobileNavText} hover:text-[#C5A880]`}>Ablauf</a>
        </nav>
        <div className="flex flex-col gap-4">
          <a
            href="#kontakt"
            onClick={closeMobileMenu}
            className="w-full text-center bg-gradient-to-r from-[#C5A880] to-[#A0825B] text-[#070A10] py-4 rounded-xl font-bold"
          >
            Diagnose-Gespräch buchen
          </a>
          <p className="text-center text-xs text-slate-500">MBW International • Europaweite Steueroptimierung</p>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="min-h-screen pt-32 pb-20 flex items-center relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7 flex flex-col gap-6 text-left">
            <div className="inline-flex items-center gap-2 bg-[#C5A880]/10 border border-[#C5A880]/20 px-3.5 py-1.5 rounded-full w-fit">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C5A880] animate-pulse" />
              <span className="text-xs font-bold tracking-wider uppercase text-[#C5A880]">Rechtssicher in Europa umstrukturieren</span>
            </div>

            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] ${theme.text}`}>
              Maximieren Sie Ihren Profit.{' '}
              <br />
              <span className="bg-gradient-to-r from-[#C5A880] via-amber-200 to-[#C5A880] bg-clip-text text-transparent">
                Minimieren Sie die Steuerlast.
              </span>{' '}
              Generieren Sie Substanz.
            </h1>

            <p className={`text-base sm:text-lg ${theme.textMuted} max-w-xl`}>
              Keine Briefkastensysteme, keine Experimente. Wir errichten hochprofitable, absolut gesetzeskonforme EU-Unternehmensstrukturen in Bulgarien. Volle Substanz mit realem Büro, Geschäftsführern und steuersparenden 10% Flat Tax.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-4">
              <a
                href="#kontakt"
                className="bg-gradient-to-r from-[#C5A880] to-[#A0825B] text-[#070A10] font-bold px-8 py-4 rounded-xl text-center hover:opacity-95 transition-opacity shadow-lg shadow-[#C5A880]/10"
              >
                Kostenloses Erstgespräch
              </a>
              <a
                href="#rechner"
                className={`border ${theme.border} hover:bg-white/5 ${theme.textMuted} px-8 py-4 rounded-xl font-semibold text-center transition-all`}
              >
                Ersparnis berechnen
              </a>
            </div>

            {/* Stats */}
            <div className={`grid grid-cols-3 gap-6 pt-8 border-t ${theme.border} mt-6`}>
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#C5A880]">10%</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Flat Tax – Gesetzlich</div>
              </div>
              <div>
                <div className={`text-2xl sm:text-3xl font-extrabold ${theme.text}`}>100%</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">EU-Compliance</div>
              </div>
              <div>
                <div className={`text-2xl sm:text-3xl font-extrabold ${theme.text}`}>24 Std.</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Reaktionszeit Gesetzlich</div>
              </div>
            </div>
          </div>

          {/* Value Card */}
          <div className="lg:col-span-5 relative">
            <div className="absolute inset-0 bg-[#C5A880]/20 rounded-3xl blur-2xl pointer-events-none" />
            <div className={`relative ${theme.cardBg} border ${theme.border} p-8 rounded-3xl shadow-2xl`}>
              <h3 className={`text-xl font-bold mb-4 ${theme.text}`}>Das MBW-Executive-Versprechen</h3>
              <ul className={`space-y-4 text-sm ${theme.textMuted}`}>
                <li className="flex items-start gap-3">
                  <span className="text-[#C5A880] font-bold">✓</span>
                  <span>
                    <strong className={theme.text}>Keine Scheinfirma:</strong> Physische Substanzvorgaben (Büroadressen, lokale Arbeitsverträge) direkt vor Ort in Varna.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#C5A880] font-bold">✓</span>
                  <span>
                    <strong className={theme.text}>Komplettes Backoffice:</strong> Vollwertige Buchhaltung, Steuerberatung und rechtliche Begleitung aus einer Hand.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#C5A880] font-bold">✓</span>
                  <span>
                    <strong className={theme.text}>Rechtssichere Gewinnausschüttung:</strong> Optimierte Gestaltung der deutschen Wegzugsteuer und Quellensteuern.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MYTHEN VS. REALITÄT */}
      <section id="mythen" className={`py-24 ${theme.sectionBg} border-y ${theme.border}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-xs font-bold tracking-widest text-[#C5A880] uppercase mb-3">Fakten statt Gerüchte</h2>
            <h3 className={`text-3xl sm:text-4xl font-extrabold ${theme.text}`}>Mythen vs. Realität im EU-Steuerrecht</h3>
            <p className={`${theme.textMuted} mt-4`}>Viele Vorurteile über Auslandsfirmen stammen aus einer veralteten Zeit. Hier ist die Realität bei MBW.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Myth 1 */}
            <div className={`${isDark ? 'bg-[#070A10]' : 'bg-white'} border ${theme.border} p-8 rounded-2xl`}>
              <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-red-500/10 text-red-400 text-xs font-bold uppercase tracking-wider mb-4">Mythos</div>
              <h4 className={`text-lg font-bold mb-3 ${theme.text}`}>"Eine Firma in Bulgarien ist automatisch eine illegale Briefkasten-Konstruktion."</h4>
              <p className={`${theme.textMuted} border-t ${theme.border} pt-4 mt-4`}>
                <span className="text-emerald-400 font-bold block mb-1">✓ Die MBW Realität:</span>
                Ein Briefkasten ist illegal. Deshalb liefert MBW echte, physische <strong>Substanz</strong> nach OECD-Richtlinien: Mietvertrag für ein physisches Büro, eigener lokaler Angestellter und echte Geschäftsführung direkt vor Ort in Varna. Das ist absolut legal und compliant.
              </p>
            </div>

            {/* Myth 2 */}
            <div className={`${isDark ? 'bg-[#070A10]' : 'bg-white'} border ${theme.border} p-8 rounded-2xl`}>
              <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-red-500/10 text-red-400 text-xs font-bold uppercase tracking-wider mb-4">Mythos</div>
              <h4 className={`text-lg font-bold mb-3 ${theme.text}`}>"Deutsche Finanzämter akzeptieren das nicht und besteuern die Gewinne nach."</h4>
              <p className={`${theme.textMuted} border-t ${theme.border} pt-4 mt-4`}>
                <span className="text-emerald-400 font-bold block mb-1">✓ Die MBW Realität:</span>
                Wir arbeiten strikt nach dem Außensteuergesetz (AStG). Durch die korrekte Ansiedlung des "Ortes der tatsächlichen Geschäftsleitung" und echter operativer Substanz erlischt der Steuerzugriff im DACH-Raum völlig konform mit geltenden EU-Gesetzen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TAX CALCULATOR */}
      <section id="rechner" className="py-24 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-xs font-bold tracking-widest text-[#C5A880] uppercase mb-3">Vorteils-Rechner</h2>
            <h3 className={`text-3xl sm:text-4xl font-extrabold ${theme.text}`}>Hier schlägt Deutschland Bulgarien um Welten</h3>
            <p className={`${theme.textMuted} mt-4`}>Berechnen Sie live Ihren steuerlichen Vorteil mit einer MBW-Kanzleistruktur.</p>
          </div>

          <div className={`${isDark ? 'bg-[#0B0F19]/80' : 'bg-white'} border ${theme.border} p-8 sm:p-12 rounded-3xl shadow-2xl`}>
            <div className="space-y-8">
              {/* Slider */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Jährlicher Gewinn vor Steuern
                  </label>
                  <span className="text-2xl font-extrabold text-[#C5A880]">{formatCurrency(profit)}</span>
                </div>
                <input
                  type="range"
                  min={50000}
                  max={2000000}
                  step={25000}
                  value={profit}
                  onChange={(e) => setProfit(parseInt(e.target.value))}
                  className={`w-full h-2 ${isDark ? 'bg-[#070A10]' : 'bg-slate-200'} rounded-lg appearance-none cursor-pointer accent-[#C5A880]`}
                />
              </div>

              {/* Results Grid */}
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t ${theme.border}`}>
                <div className={`p-6 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-slate-100'} border ${theme.border}`}>
                  <span className={`text-xs font-bold uppercase tracking-wider ${theme.textMuted} block mb-1`}>Steuerlast Deutschland (Ø 42%)</span>
                  <div className="text-2xl font-extrabold text-red-400 mb-2">{formatCurrency(gerTax)}</div>
                  <p className="text-xs text-slate-500">Inklusive Gewerbe-, Körperschaftssteuer und Soli.</p>
                </div>

                <div className="p-6 rounded-2xl bg-[#C5A880]/10 border border-[#C5A880]/20">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#C5A880] block mb-1">Steuerlast Bulgarien (10%)</span>
                  <div className="text-2xl font-extrabold text-emerald-400 mb-2">{formatCurrency(bulgTax)}</div>
                  <p className={`text-xs ${theme.textSubtle}`}>Optimierte steuerliche Flat-Rate durch MBW-Planung.</p>
                </div>
              </div>

              {/* Total Win */}
              <div className="p-6 rounded-2xl bg-gradient-to-r from-[#C5A880] to-[#A0825B] text-[#070A10] flex flex-col sm:flex-row justify-between items-center gap-4">
                <div>
                  <span className="text-xs uppercase font-extrabold tracking-wider opacity-80">Ihr jährlicher Cashflow-Gewinn:</span>
                  <div className="text-3xl sm:text-4xl font-extrabold">{formatCurrency(totalSavings)}</div>
                </div>
                <a
                  href="#kontakt"
                  className="bg-[#070A10] text-slate-100 hover:opacity-90 font-bold px-6 py-3 rounded-xl text-sm transition-all"
                >
                  Sichern Sie sich diesen Vorteil
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS STEPS */}
      <section id="prozess" className={`py-24 ${isDark ? 'bg-[#0B0F19]/20' : 'bg-slate-100'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-xs font-bold tracking-widest text-[#C5A880] uppercase mb-3">Die Systematik</h2>
            <h3 className={`text-3xl sm:text-4xl font-extrabold ${theme.text}`}>In 4 Schritten zur steuerfreien Zone</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                num: '01',
                title: 'Strukturanalyse',
                desc: 'Wir prüfen Ihr Geschäftsmodell detailliert auf Substanzvorgaben und steuerliche Haltepflichten im Heimatland.',
              },
              {
                num: '02',
                title: 'Setup & Gründung',
                desc: 'Gründung der bulgarischen GmbH (OOD) mit physischem Büro, Verträgen und Banken-Eröffnungen.',
              },
              {
                num: '03',
                title: 'Substanznachweise',
                desc: 'Lokale Mitarbeiter, Verträge und reale Rechnungsstellung nach OECD-Richtlinien vor Ort.',
              },
              {
                num: '04',
                title: 'Rechtssicherer Exit',
                desc: 'Verlagerung der Gewinne, sichere Wegzugsteuerregelung und Einbindung der neuen Holdingstruktur.',
              },
            ].map((step) => (
              <div key={step.num} className="flex flex-col gap-4">
                <div className="text-5xl font-extrabold text-[#C5A880]/30">{step.num}</div>
                <h4 className={`text-lg font-bold ${theme.text}`}>{step.title}</h4>
                <p className={`text-sm ${theme.textMuted}`}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="kontakt" className={`py-24 border-t ${theme.border}`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-xs font-bold tracking-widest text-[#C5A880] uppercase mb-3">Direkter Draht</h2>
            <h3 className={`text-3xl sm:text-4xl font-extrabold ${theme.text}`}>Starten Sie Ihr steuerliches Gutachten</h3>
            <p className={`${theme.textMuted} mt-4`}>Tragen Sie sich jetzt für eine kostenlose, vertrauliche Analyse Ihrer steuerlichen Situation ein.</p>
          </div>

          <ContactForm isDark={isDark} theme={theme} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className={`${isDark ? 'bg-[#070A10]' : 'bg-white'} border-t ${theme.border} py-12 text-slate-500`}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <span className={`text-sm font-bold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>MBW International</span>
            <span className="text-xs">|</span>
            <span className="text-[10px] uppercase tracking-wider">Kanzlei für EU-Strukturierungen</span>
          </div>

          <div className={`flex gap-6 text-xs ${theme.navText}`}>
            <a href="#" className="hover:text-[#C5A880]">Datenschutz</a>
            <a href="#" className="hover:text-[#C5A880]">Impressum</a>
            <a href="#" className="hover:text-[#C5A880]">Haftungsausschluss</a>
          </div>

          <p className="text-[10px]">&copy; 2026 MBW International Ltd. (Varna, Bulgarien). Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
}

interface ThemeConfig {
  bg: string;
  text: string;
  textMuted: string;
  textSubtle: string;
  border: string;
  cardBg: string;
  sectionBg: string;
  inputBg: string;
  headerBg: string;
  mobileMenuBg: string;
  navText: string;
  mobileNavText: string;
}

function ContactForm({ isDark, theme }: { isDark: boolean; theme: ThemeConfig }) {
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  const inputClass = `w-full ${theme.inputBg} border ${theme.border} rounded-xl px-5 py-4 ${theme.text} focus:outline-none focus:border-[#C5A880]`;

  if (submitted) {
    return (
      <div className={`${isDark ? 'bg-[#0B0F19]/60' : 'bg-white'} border ${theme.border} p-12 rounded-3xl text-center`}>
        <div className="text-4xl mb-4">✓</div>
        <h4 className={`text-xl font-bold mb-2 ${theme.text}`}>Danke!</h4>
        <p className={theme.textMuted}>Ihre Anfrage wurde verschlüsselt an unsere Kanzlei übermittelt. Wir melden uns innerhalb von 24 Stunden.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${isDark ? 'bg-[#0B0F19]/60' : 'bg-white'} border ${theme.border} p-8 sm:p-12 rounded-3xl`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={`block text-xs uppercase tracking-wider ${theme.textMuted} font-bold mb-2`}>Vor- & Nachname</label>
          <input type="text" required className={inputClass} placeholder="Maximilian Schmidt" />
        </div>
        <div>
          <label className={`block text-xs uppercase tracking-wider ${theme.textMuted} font-bold mb-2`}>E-Mail-Adresse (Privat / Sicher)</label>
          <input type="email" required className={inputClass} placeholder="m.schmidt@secure.de" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={`block text-xs uppercase tracking-wider ${theme.textMuted} font-bold mb-2`}>Telefonnummer (Signal / Telegram bevorzugt)</label>
          <input type="text" required className={inputClass} placeholder="+49 170 1234567" />
        </div>
        <div>
          <label className={`block text-xs uppercase tracking-wider ${theme.textMuted} font-bold mb-2`}>Aktueller Jahresprofit (€)</label>
          <select className={inputClass}>
            <option>Unter 100.000 €</option>
            <option>100.000 € - 250.000 €</option>
            <option>250.000 € - 500.000 €</option>
            <option>Über 500.000 €</option>
          </select>
        </div>
      </div>

      <div>
        <label className={`block text-xs uppercase tracking-wider ${theme.textMuted} font-bold mb-2`}>Aktuelle Unternehmenssituation / Zielsetzung</label>
        <textarea rows={4} className={inputClass} placeholder="Planen Sie Wegzug, Standorterweiterung oder Gründung einer Holding?" />
      </div>

      <div className="flex items-start gap-3 mt-4">
        <input type="checkbox" required className="mt-1 accent-[#C5A880]" />
        <span className="text-xs text-slate-500">
          Ich stimme zu, dass meine Daten absolut vertraulich und verschlüsselt zur Kontaktaufnahme gespeichert werden dürfen.
        </span>
      </div>

      <button
        type="submit"
        className="w-full bg-gradient-to-r from-[#C5A880] to-[#A0825B] text-[#070A10] font-extrabold uppercase tracking-wider py-5 rounded-xl text-sm hover:opacity-95 transition-opacity shadow-lg shadow-[#C5A880]/10"
      >
        Sicheres Kanzlei-Inbound starten
      </button>
    </form>
  );
}
